// lodash
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import startsWith from 'lodash/startsWith';

import {
  APPLE_PAY_BILLING_ADDRESS_MISSING,
  APPLE_PAY_PAYMENT,
  APPLE_PAY_PAYMENT_CANCEL,
  APPLE_PAY_PAYMENT_FAILURE,
  APPLE_PAY_REQUEST_PAYMENT_SESSION,
  APPLE_PAY_SELECTED,
  APPLE_PAY_SET_PAYMENT_REQUEST,
  APPLE_PAY_SUBMIT_PAYMENT_DATA,
  APPLY_GIFT_CARD,
  APPLY_GIFT_VOUCHER,
  APPLY_PROMO_CODE,
  APPS_AUTHENTICATED_HANDOVER,
  APPS_GUEST_HANDOVER,
  PAYPAL_STUBS_MODAL_CLOSE,
  PAYPAL_STUBS_MODAL_OPEN,
  CARD_PAYMENT,
  CARD_TYPE_MISSING,
  CHANGE_PAYMENT_DETAILS,
  CLOSE_POS_CREDIT_GET_READY_MODAL,
  CREATE_GOOGLE_PAY_PAYMENT,
  CREATE_PAYPAL_PAYMENT,
  GET_DELIVERY_PAGE,
  GET_ORDER_CONFIRMATION_PAGE,
  GET_PACT_PAYMENT_DATA,
  GET_PAYMENT_PAGE,
  GET_PAYMENT_WALLET,
  GET_PAYPAL_CLIENT_TOKEN,
  GIFT_OPTIONS_CHANGE_OPTION,
  GIFT_OPTIONS_CHANGE_TO_PREPAID,
  GOOGLE_PAY_CANCEL,
  GOOGLE_PAY_ENABLED,
  GOOGLE_PAY_DEFAULT,
  GOOGLE_PAY_PAYMENT_DATA_ERROR,
  GOOGLE_PAY_PAYMENT_DATA_RECEIVED,
  GOOGLE_PAY_STUBS_MODAL_OPEN,
  GOOGLE_PAY_SUBMIT_PAYMENT_DATA,
  GOOGLE_PAY_WALLET_ELIGIBLE,
  GOOGLE_SCRIPT_FAILED,
  GOOGLE_SCRIPT_LOADED,
  GOOGLE_SCRIPT_LOADING,
  HAND_OVER,
  INIT_PAYMENT_PAGE,
  IOVATION_SCRIPTS_FAILED,
  IOVATION_SCRIPTS_LOADED,
  IOVATION_SCRIPTS_LOADING,
  LOGIN_RESET,
  OCP_SUBMIT_3DS_METHOD,
  OCP_SUBMIT_3DS_METHOD_ERROR,
  PACT_CARD_PAYMENT,
  PACT_PAYMENT_CANCEL,
  PACT_PAYMENT_ERROR,
  PACT_PAYMENT_INIT,
  PACT_PAYMENT_SUCCESS,
  PAYMENT_CARD_DELETE,
  PAYMENT_NOT_SELECTED,
  PAYPAL_PAYMENT_CANCEL,
  PAYPAL_PAYMENT_ENDED,
  PAYPAL_PAYMENT_FAILED,
  PAYPAL_PAYMENT_STARTED,
  POS_CREDIT_PAYMENT,
  POST_DELIVERY_PAGE,
  REDUX_FORM_SET_SUBMIT_FAILED,
  REDUX_HYDRATE,
  REMOVE_PROMO_CODE,
  REMOVE_GIFT_OPTION,
  RESET_SUBMIT_ORDER_ERROR,
  RESERVE_STOCK,
  ROUTER_LOCATION_CHANGE,
  SET_APPLE_PAY_PAYMENT_REQUEST,
  SET_CAN_MAKE_APPLE_PAY_PAYMENTS,
  SET_CAN_MAKE_GOOGLE_PAY_PAYMENTS,
  SET_CARD_PAYMENT_3DSECURE_CANCELLED_STATUS,
  SET_CHECK_ELIGIBILITY_PROCESSING,
  SET_OCP_3DS_FAIL_ERROR,
  SET_PARTNER_DISCOUNT,
  SET_PAYMENT_PROCESSING,
  SET_PAYMENT_TYPE,
  SET_PAYPAL_SERVICE_UNAVAILABLE,
  SET_PROMO_CODE_ERROR,
  SET_SELECTED_PAYMENT_CATEGORY,
  SET_SELECTED_SAVED_CARD,
  SHOW_POS_CREDIT_GET_READY_MODAL,
  SHOW_POS_CREDIT_PROVIDER_UNAVAILABLE_ERROR,
  SUBMIT_ORDER,
  UPDATE_BILLING_ADDRESS,
} from '../../../constants/actionConstants';
import { PACT } from '../../../constants/applicationTypeConstants';
import PROGRESS_STATE from '../../../constants/progressStateConstants';
import errorCodeConstants from '../../../constants/errorCodeConstants';
import paymentTypeConstants from '../../../constants/paymentTypeConstants';
import routeConstants from '../../../constants/routeConstants';
import { getNonGlobalError } from '../../../utils/error/parseError';
import { getDefaultPaymentCardOrFirst } from '../user/userReducer';
import paymentStatusConstants from '../../../constants/paymentStatusConstants';
import isSavedPaymentCard from '../../../utils/payment/isSavedPaymentCard';
import paymentCategoryConstants from '../../../constants/paymentCategoryConstants';
import supportedCreditCardTypes from '../../../constants/supportedCreditCardTypes';

export const INITIAL_STATE = {
  alreadyLoaded: false,
  applePayCompatibilityChecksComplete: false,
  applePayPaymentRequest: undefined,
  applePayPaymentSessionApiCallActive: false,
  applePayResponse: undefined,
  billingAddress: undefined,
  payPalStubsModalOpen: undefined,
  cancelPaypalRedirect: false,
  canMakeGooglePayPayments: false,
  creditCard3DSecureInfo: {},
  creditTypeGroups: [],
  getPaymentPageCallActive: false,
  giftOptionsModalContentId: '',
  giftOptionsModalOpen: undefined,
  googlePayBaseCardPaymentMethod: {},
  googlePayCardPaymentMethod: {},
  googlePayPaymentData: undefined,
  googlePayPaymentsClientHasBeenCreated: false,
  googlePayStubsModalOpen: undefined,
  googlePayWalletEligible: false,
  initPaymentPageCallActive: false,
  invalidGooglePayBillingAddress: undefined,
  ocpPaymentId: '',
  pactOrderFailed: false,
  pactPaymentInitData: undefined,
  pactPaymentMessage: undefined,
  pactPaymentModalOpen: false,
  pactPaymentPedResponseData: undefined,
  pactPaymentStatus: PROGRESS_STATE.NONE,
  partnerDiscountInvalid: false,
  paymentCardDeleteApiCallActive: false,
  paymentCardDeleteApiCallFailed: false,
  paymentCardDeleted: undefined,
  paymentFormsValidationError: undefined,
  paymentNotSelectedError: undefined,
  paymentNotSelectedErrorMinor: undefined,
  paymentProcessing: false,
  paymentWallet: undefined,
  payOptionsAnalytics: undefined,
  payPalAuthorisationUnavailable: false,
  payPalServiceUnavailable: undefined,
  paypalUnavailableError: undefined,
  posCreditEligible: false,
  savedPaymentFormsValidationError: undefined,
  selectedPaymentCategory: undefined,
  selectedPaymentType: null,
  shouldShowPaymentCardDeleteModal: false,
  show3dSecureFrame: false,
  show3dsMethodFrame: false,
  showAddressBookModal: false,
  showMarketingPreferencesPrompt: undefined,
  streamlinedPaymentsFeatureEnabled: false,
  submitOrderApiCallActive: false,
  submitOrderError: undefined,
  submitOrderLoading: false,
  threeDSMethodInfo: {},
};

export function posCreditNoLongerEligible(posCreditEligible, selectedPaymentType) {
  return !posCreditEligible && includes(selectedPaymentType, paymentTypeConstants.POS_CREDIT);
}

export function determineSelectedPaymentType(params) {
  const {
    action,
    selectNewPaymentType,
    state = {},
  } = params;

  const {
    applePayCompatibilityChecksComplete,
    canMakeGooglePayPayments,
    creditTypeGroups,
    googlePayPaymentsClientHasBeenCreated,
    selectedPaymentType,
    paymentWallet = [],
    posCreditEligible,
  } = state;

  const {
    category,
  } = action;

  if (!selectedPaymentType || selectNewPaymentType) {

    const isPartnerDiscountEnabled = get(action, 'isPartnerDiscountEnabled');
    const validPaymentWallet = get(action, 'result.paymentWallet', paymentWallet);
    const defaultSavedPaymentCard = getDefaultPaymentCardOrFirst(validPaymentWallet);

    if (!category || category === paymentCategoryConstants.PAY_NOW) {
      if (applePayCompatibilityChecksComplete) {
        return paymentTypeConstants.APPLE_PAY;
      }

      if (canMakeGooglePayPayments && googlePayPaymentsClientHasBeenCreated) {
        return paymentTypeConstants.GOOGLE_PAY;
      }

      if (isEmpty(validPaymentWallet) && isPartnerDiscountEnabled) {
        return paymentTypeConstants.CREDIT_CARD;
      }

      if (defaultSavedPaymentCard) {
        return `${paymentTypeConstants.SAVED_CREDIT_CARD}:${defaultSavedPaymentCard.id}`;
      }
    }

    if (!category || category === paymentCategoryConstants.PAY_LATER) {
      if (posCreditEligible && creditTypeGroups.length === 1) {
        return `${paymentTypeConstants.POS_CREDIT}:${creditTypeGroups[0]?.id}`;
      }
    }
  }

  return selectedPaymentType;
}

export default function paymentReducer(state = INITIAL_STATE, action = {}) {
  switch (action.type) {
    case REDUX_HYDRATE: {
      const payload = get(action, 'payload.payment');

      return {
        ...payload,
        submitOrderApiCallActive: false,
      };
    }

    case `${APPS_GUEST_HANDOVER}.SUCCESS`:
    case `${APPS_AUTHENTICATED_HANDOVER}.SUCCESS`:
    case `${HAND_OVER}.SUCCESS`: {
      const applicationType = get(action.result, 'applicationType');
      const isPACT = applicationType === PACT;
      return {
        ...state,
        selectedPaymentType: isPACT ? paymentTypeConstants.PACT : '',
      };
    }
    case LOGIN_RESET:
      return {
        ...INITIAL_STATE,
        selectedPaymentType: state.selectedPaymentType === paymentTypeConstants.PACT ? state.selectedPaymentType : null,
      };

    case `${SET_CHECK_ELIGIBILITY_PROCESSING}.TRUE`:
      return {
        ...state,
        checkPOSCreditEligibilityProcessing: true,
      };

    case `${SET_CHECK_ELIGIBILITY_PROCESSING}.FALSE`:
      return {
        ...state,
        checkPOSCreditEligibilityProcessing: false,
      };

    case `${SET_PAYMENT_PROCESSING}.TRUE`:
      return {
        ...state,
        paymentProcessing: true,
        submitOrderError: undefined,
      };

    case `${SET_PAYMENT_PROCESSING}.FALSE`:
      return {
        ...state,
        paymentProcessing: false,
      };

    case `${SUBMIT_ORDER}.LOADING`:
      return {
        ...state,
        paymentProcessing: true,
        submitOrderApiCallActive: true,
      };

    case OCP_SUBMIT_3DS_METHOD_ERROR:
      return {
        ...state,
        show3dsMethodFrame: false,
        submitOrderError: getNonGlobalError(action),
      };

    case `${OCP_SUBMIT_3DS_METHOD}.LOADING`:
      return {
        ...state,
        show3dsMethodFrame: false,
      };

    case `${OCP_SUBMIT_3DS_METHOD}.SUCCESS`: {
      const status = get(action.result, 'status');
      const challengeCheck = get(action.result, 'challengeCheck', {});

      if (status === paymentStatusConstants.PENDING_CHALLENGE) {
        const { actionUrl, formData } = challengeCheck;

        return {
          ...state,
          creditCard3DSecureInfo: {
            is3DSecureAvailable: true,
            creditCard3DSecureFormId: 'paymentForm',
            creditCard3DSecureMerchantUrl: actionUrl,
            creditCard3DSecureFormData: formData,
          },
          submitOrderError: undefined,
          show3dSecureFrame: true,
        };
      }

      return state;
    }

    case `${SUBMIT_ORDER}.SUCCESS`:
      return {
        ...state,
        paymentProcessing: false,
        submitOrderApiCallActive: false,
      };

    case `${UPDATE_BILLING_ADDRESS}.LOADING`: {
      return {
        ...state,
        billingAddress: undefined,
        paymentProcessing: true,
      };
    }

    case `${UPDATE_BILLING_ADDRESS}.SUCCESS`: {
      if (state.paypalNonce || state.invalidPayPalBillingAddress) {
        return {
          ...state,
          billingAddress: action.billingAddress,
          paypalNonce: undefined,
          invalidPayPalBillingAddress: undefined,
        };
      }

      if (state.invalidGooglePayBillingAddress) {
        return {
          ...state,
          billingAddress: action.billingAddress,
          invalidGooglePayBillingAddress: undefined,
        };
      }

      return {
        ...state,
        billingAddress: action.billingAddress,
      };
    }

    // TODO reduce complexity here as there's lots of different scenarios happening in one block
    case APPLE_PAY_BILLING_ADDRESS_MISSING:
    case `${UPDATE_BILLING_ADDRESS}.FAILED`:
    case `${PACT_CARD_PAYMENT}.FAILED`:
    case `${SUBMIT_ORDER}.FAILED`:
    case `${CARD_PAYMENT}.FAILED`:
    case `${GET_ORDER_CONFIRMATION_PAGE}.FAILED`:
    case `${RESERVE_STOCK}.FAILED`: {
      let selectedPaymentType = state.selectedPaymentType;
      let payPalAuthorisationUnavailable = state.payPalAuthorisationUnavailable;
      if (action.type === `${SUBMIT_ORDER}.FAILED` || action.type === `${PACT_CARD_PAYMENT}.FAILED`) {
        const isPactPaymentType = selectedPaymentType === paymentTypeConstants.PACT;
        const isPosCreditType = includes(selectedPaymentType, paymentTypeConstants.POS_CREDIT);

        selectedPaymentType =
          selectedPaymentType ===
            paymentTypeConstants.APPLE_PAY ||
            isSavedPaymentCard(selectedPaymentType) ||
            isPactPaymentType ||
            isPosCreditType
            ? selectedPaymentType : paymentTypeConstants.CREDIT_CARD;
      }

      if (action.isPactApp && (
        action.type === `${SUBMIT_ORDER}.FAILED` ||
        action.type === `${PACT_CARD_PAYMENT}.FAILED` ||
        action.type === `${GET_ORDER_CONFIRMATION_PAGE}.FAILED`
      )) {
        return {
          ...state,
          paymentProcessing: false,
          show3dSecureFrame: false,
          selectedPaymentType,
          submitOrderApiCallActive: false,
          pactPaymentModalOpen: false,
          pactOrderFailed: true,
        };
      }

      if (action.error?.code === errorCodeConstants.PARTNER_CARD_NUMBER_INVALID) {
        return {
          ...state,
          paymentProcessing: false,
          show3dSecureFrame: false,
          selectedPaymentType,
          submitOrderApiCallActive: false,
          partnerDiscountInvalid: true,
          promoCodeError: null,
        };
      }

      if (action.error?.code === errorCodeConstants.PAYMENT_POS_CREDIT_DECLINED_ERROR ||
          action.error?.code === errorCodeConstants.PAYMENT_POS_CREDIT_CANCELLED_ERROR) {
        selectedPaymentType = undefined;
      }

      if (action.error?.code === errorCodeConstants.PAYPAL_AUTHORISATION_DECLINED ||
          action.error?.code === errorCodeConstants.PAYPAL_AUTHORISATION_DECLINED_WITH_RETRY_PERMITTED ||
          action.error?.code === errorCodeConstants.PAYMENT_SERVICE_PAYPAL_AUTHORISATION_FAILED) {
        selectedPaymentType = undefined;
      }

      if (action.error?.code === errorCodeConstants.PAYMENT_SERVICE_PAYPAL_AUTHORISATION_UNAVAILABLE ||
          action.error?.code === errorCodeConstants.PAYMENT_SERVICE_PAYPAL_FRAUD_DATA_UNAVAILABLE ||
          action.error?.code === errorCodeConstants.PAYMENT_SERVICE_PAYPAL_FRAUD_DATA_FAILED) {
        payPalAuthorisationUnavailable = true;
        selectedPaymentType = undefined;
      }

      const isPaypalBillingAddressInvalid = action.type === `${UPDATE_BILLING_ADDRESS}.FAILED` &&
        selectedPaymentType === paymentTypeConstants.PAYPAL;

      const isGooglePayBillingAddressInvalid = action.type === `${UPDATE_BILLING_ADDRESS}.FAILED` &&
        selectedPaymentType === paymentTypeConstants.GOOGLE_PAY;

      const submitOrderError = (isPaypalBillingAddressInvalid || isGooglePayBillingAddressInvalid) ?
        null : getNonGlobalError(action, state.submitOrderError, {
          isPosCreditSubmissionError: includes(action.selectedPaymentType, paymentTypeConstants.POS_CREDIT),
        });

      if (includes([
        errorCodeConstants.PAYMENT_POS_CREDIT_DECLINED_ERROR,
        errorCodeConstants.PAYMENT_POS_CREDIT_CANCELLED_ERROR,
        errorCodeConstants.PAYMENT_POS_CREDIT_APPLICATION_INCOMPLETE,
      ], action.error?.code)) {
        submitOrderError.isPosCreditError = true;
      }

      return {
        ...state,
        paymentProcessing: false,
        submitOrderError,
        show3dSecureFrame: false,
        selectedPaymentType,
        submitOrderApiCallActive: false,
        payPalAuthorisationUnavailable,
        promoCodeError: null,
        ...isPaypalBillingAddressInvalid && {
          invalidPayPalBillingAddress: get(action, 'billingAddressFromPaypal'),
          paypalNonce: get(action, 'paypalNonce'),
        },
        ...isGooglePayBillingAddressInvalid && {
          invalidGooglePayBillingAddress: get(action, 'googlePayBillingAddress'),
        },
      };
    }

    case `${CARD_PAYMENT}.SUCCESS`: {
      const threeDSMethodInfo = get(action.result, 'threeDSMethodInfo', {});
      const ocpPaymentId = get(action.result, 'ocpPaymentId');
      const isThreeDSMethodRequired = threeDSMethodInfo.threeDSMethodRequired;
      if (isThreeDSMethodRequired) {
        return {
          ...state,
          ocpPaymentId,
          threeDSMethodInfo,
          submitOrderError: undefined,
          show3dsMethodFrame: isThreeDSMethodRequired,
        };
      }

      const creditCard3DSecureInfo = get(action.result, 'creditCard3DSecureInfo', {});
      const is3DSecureAvailable = creditCard3DSecureInfo.is3DSecureAvailable;
      const transformedCreditCard3DSecureInfo = is3DSecureAvailable ? {
        creditCard3DSecureFormId: 'paymentForm',
        ...creditCard3DSecureInfo,
      } : creditCard3DSecureInfo;

      return {
        ...state,
        creditCard3DSecureInfo: transformedCreditCard3DSecureInfo,
        submitOrderError: undefined,
        show3dSecureFrame: is3DSecureAvailable,
      };
    }

    case SET_CARD_PAYMENT_3DSECURE_CANCELLED_STATUS: {
      const error = getNonGlobalError(
        { error: { code: errorCodeConstants.CLIENT_PAYMENT_3DS_CANCELLED_ERROR } },
        state.submitOrderError,
      );

      return {
        ...state,
        show3dSecureFrame: false,
        submitOrderError: error,
        showPartnerCardError: action?.cardType === supportedCreditCardTypes.NEW_DAY,
        paymentProcessing: false,
      };
    }

    case RESET_SUBMIT_ORDER_ERROR:
      return {
        ...state,
        submitOrderError: undefined,
      };

    case APPLE_PAY_SELECTED: {
      return {
        ...state,
        payOptionsAnalytics: {
          ...state.payOptionsAnalytics,
          [paymentTypeConstants.APPLE_PAY]: {
            default: 1,
            enabled: 1,
          },
        },
      };
    }

    case GOOGLE_PAY_ENABLED: {
      return {
        ...state,
        payOptionsAnalytics: {
          ...state.payOptionsAnalytics,
          [paymentTypeConstants.GOOGLE_PAY]: {
            default: 0,
            enabled: 1,
          },
        },
      };
    }

    case GOOGLE_PAY_DEFAULT: {
      return {
        ...state,
        payOptionsAnalytics: {
          ...state.payOptionsAnalytics,
          [paymentTypeConstants.GOOGLE_PAY]: {
            default: 1,
            enabled: 1,
          },
        },
      };
    }

    case SET_PAYMENT_TYPE: {
      const { paymentType, posCreditType } = action;

      const paymentTypesAnalyticsMappings = {
        [paymentTypeConstants.APPLE_PAY]: 'applePay',
        [paymentTypeConstants.PAYPAL]: 'payPal',
        [paymentTypeConstants.CREDIT_CARD]: 'cc_new',
        [paymentTypeConstants.SAVED_CREDIT_CARD]: 'cc_saved',
        [paymentTypeConstants.GOOGLE_PAY]: 'googlePay',
      };

      const parsedPaymentType = paymentType.split(':')[0];
      const newPaymentTypeParsed = parsedPaymentType === paymentTypeConstants.POS_CREDIT ?
        `posc_${posCreditType}` :
        paymentTypesAnalyticsMappings[parsedPaymentType];

      const updatedPayOptionsAnalytics = {};

      // below required to temporarily allow for multiple selected items to be set to 0 until CC is removed. See MARV-10325
      Object.keys(state.payOptionsAnalytics).forEach((paymentType) => {
        const paymentOption = state.payOptionsAnalytics[paymentType];
        updatedPayOptionsAnalytics[paymentType] = {
          ...paymentOption,
          selected: paymentOption.selected === 1 ? 0 : paymentOption.selected, // payment option previously selected
        };
      });

      // ccAnalyticsMapping added temporarily while we maintain CC awaiting an update from Analytics.  See MARV-10325
      const ccAnalyticsMapping = startsWith(newPaymentTypeParsed, 'cc_') ? {
        ...updatedPayOptionsAnalytics,
        CC: {
          ...state.payOptionsAnalytics.CC,
          selected: 1,
        },
      } : updatedPayOptionsAnalytics;

      const newPayOptionsAnalytics = {
        ...ccAnalyticsMapping,
        [newPaymentTypeParsed]: {
          ...state.payOptionsAnalytics[newPaymentTypeParsed],
          selected: 1, // payment option currently selected
        },
      };

      return {
        ...state,
        selectedPaymentType: paymentType,
        submitOrderError: undefined,
        paymentNotSelectedError: undefined,
        paymentNotSelectedErrorMinor: undefined,
        payOptionsAnalytics: newPayOptionsAnalytics,
      };
    }

    case `${GET_PAYMENT_PAGE}.LOADING`: {
      return {
        ...state,
        getPaymentPageCallActive: true,
        paymentCardDeleted: undefined,
        paymentCardDeleteApiCallFailed: false,
      };
    }

    case `${GET_PAYMENT_PAGE}.FAILED`: {
      return {
        ...state,
        getPaymentPageCallActive: false,
      };
    }

    case `${INIT_PAYMENT_PAGE}.LOADING`: {
      return {
        ...state,
        initPaymentPageCallActive: true,
        paymentCardDeleted: undefined,
        paymentCardDeleteApiCallFailed: false,
      };
    }

    case `${INIT_PAYMENT_PAGE}.FAILED`: {
      return {
        ...state,
        initPaymentPageCallActive: false,
      };
    }

    case `${APPLY_PROMO_CODE}.SUCCESS`:
    case `${REMOVE_PROMO_CODE}.SUCCESS`: {
      const posCreditEligible = get(action, 'result.creditPaymentDetails.displayOption', false);

      const selectedPaymentType = determineSelectedPaymentType({
        action,
        state,
        selectNewPaymentType: posCreditNoLongerEligible(posCreditEligible, state.selectedPaymentType),
      });

      return {
        ...state,
        selectedPaymentType,
        posCreditEligible,
        creditTypeGroups: get(action, 'result.creditPaymentDetails.creditTypeGroups', []),
        payPalPaymentRequest: get(action, 'result.payPalPaymentRequest'),
      };
    }

    case `${GET_PAYMENT_PAGE}.SUCCESS`:
    case `${INIT_PAYMENT_PAGE}.SUCCESS`: {
      const payOptionsAnalytics = get(action, 'result.analytics.checkout.payment.payOptions');
      const posCreditEligible = get(action, 'result.creditPaymentDetails.displayOption', false);
      const giftingPaymentDetails = get(action, 'result.giftingPaymentDetails');
      const streamlinedPaymentsFeatureEnabled = get(action, 'streamlinedPaymentsFeatureEnabled', false);

      const selectedPaymentType = determineSelectedPaymentType({
        action,
        state,
        selectNewPaymentType: posCreditNoLongerEligible(posCreditEligible, state.selectedPaymentType),
      });

      return {
        ...state,
        cancelPaypalRedirect: false,
        paymentProcessing: false,
        initPaymentPageCallActive: false,
        getPaymentPageCallActive: false,
        selectedPaymentType:
          state.applePayCompatibilityChecksComplete ?
            paymentTypeConstants.APPLE_PAY : selectedPaymentType,
        showMarketingPreferencesPrompt: get(action, 'result.marketingPreferencesPromptVisible'),
        posCreditEligible,
        payOptionsAnalytics,
        creditTypeGroups: get(action, 'result.creditPaymentDetails.creditTypeGroups', []),
        ...giftingPaymentDetails && { giftingPaymentDetails },
        alreadyLoaded: !!action.pushAfter,
        streamlinedPaymentsFeatureEnabled,
        payPalPaymentRequest: get(action, 'result.payPalPaymentRequest'),
      };
    }

    case `${PAYMENT_CARD_DELETE}.LOADING`: {
      return {
        ...state,
        paymentCardDeleteApiCallActive: true,
        paymentCardDeleteApiCallFailed: false,
        paymentCardDeleted: undefined,
      };
    }

    case `${PAYMENT_CARD_DELETE}.FAILED`: {
      if (get(action, 'error.code') === errorCodeConstants.CLIENT_PAYMENT_CARD_NOT_DELETED) {
        const last4Digits = get(action, 'last4Digits');

        return {
          ...state,
          paymentCardDeleteApiCallActive: false,
          paymentCardDeleteApiCallFailed: false,
          paymentCardDeleted: last4Digits,
        };
      }

      return {
        ...state,
        paymentCardDeleteApiCallActive: false,
        paymentCardDeleteApiCallFailed: true,
        paymentCardDeleted: undefined,
      };
    }

    case `${PAYMENT_CARD_DELETE}.SUCCESS`: {
      const last4Digits = get(action, 'last4Digits');
      const selectedPaymentType = determineSelectedPaymentType({
        action,
        state,
        selectNewPaymentType: true,
      });

      return {
        ...state,
        selectedPaymentType,
        paymentCardDeleteApiCallActive: false,
        paymentCardDeleteApiCallFailed: false,
        paymentCardDeleted: last4Digits,
        paymentWallet: get(action, 'result.paymentWallet', []),
      };
    }

    case `${GET_PAYMENT_WALLET}.SUCCESS`: {
      const selectedPaymentType = determineSelectedPaymentType({
        action,
        state,
      });

      return {
        ...state,
        selectedPaymentType,
        paymentWallet: get(action, 'result.paymentWallet', []),
      };
    }

    case `${PAYMENT_NOT_SELECTED}.MAJOR`:
      return {
        ...state,
        paymentNotSelectedError: getNonGlobalError(action, state.paymentNotSelectedError),
        paymentNotSelectedErrorMinor: undefined,
      };

    case `${PAYMENT_NOT_SELECTED}.MINOR`:
      return {
        ...state,
        paymentNotSelectedError: undefined,
        paymentNotSelectedErrorMinor: getNonGlobalError(action),
      };

    case SET_OCP_3DS_FAIL_ERROR:
      return {
        ...state,
        submitOrderError: getNonGlobalError(
          { error: { code: errorCodeConstants.CLIENT_PAYMENT_OCP_3DS_INVALID_ERROR } },
          state.submitOrderError,
        ),
        show3dSecureFrame: false,
      };

    case GIFT_OPTIONS_CHANGE_OPTION:
      return {
        ...state,
        giftOptionsModalContentId: action.contentId,
      };

    case GIFT_OPTIONS_CHANGE_TO_PREPAID:
      return {
        ...state,
        initialValuesForCardNumber: action.initialValues,
      };

    case `${APPLY_GIFT_CARD}.LOADING`:
    case `${APPLY_GIFT_VOUCHER}.LOADING`:
      return {
        ...state,
        applyGiftPaymentFailed: false,
      };

    case `${APPLY_GIFT_CARD}.SUCCESS`:
    case `${APPLY_GIFT_VOUCHER}.SUCCESS`:
      return {
        ...state,
        applyGiftPaymentFailed: false,
        payPalPaymentRequest: get(action, 'result.payPalPaymentRequest'),
      };

    case `${APPLY_GIFT_CARD}.FAILED`:
    case `${APPLY_GIFT_VOUCHER}.FAILED`:
      return {
        ...state,
        applyGiftPaymentFailed: true,
      };

    case `${REMOVE_GIFT_OPTION}.SUCCESS`:
      return {
        ...state,
        payPalPaymentRequest: get(action, 'result.payPalPaymentRequest'),
      };

    case CHANGE_PAYMENT_DETAILS:
      return {
        ...state,
        selectedPaymentType: null,
      };

    case PACT_PAYMENT_INIT:
      return {
        ...state,
        pactPaymentStatus: PROGRESS_STATE.LOADING,
        pactPaymentMessage: {
          title: 'Please complete payment using the card reader',
        },
      };

    case PACT_PAYMENT_CANCEL:
      return {
        ...state,
        pactPaymentStatus: PROGRESS_STATE.NONE,
        pactPaymentMessage: undefined,
        paymentProcessing: false,
        pactPaymentModalOpen: false,
      };

    case PACT_PAYMENT_ERROR:
      return {
        ...state,
        pactPaymentStatus: PROGRESS_STATE.FAILED,
        pactPaymentMessage: action.payload,
        paymentProcessing: false,
      };

    case PACT_PAYMENT_SUCCESS:
      return {
        ...state,
        pactPaymentStatus: PROGRESS_STATE.SUCCESS,
        pactPaymentMessage: {
          title: 'Processing your order',
        },
        pactPaymentPedResponseData: action.payload,
      };

    case `${GET_PACT_PAYMENT_DATA}.SUCCESS`: {
      return {
        ...state,
        pactPaymentInitData: get(action, 'result'),
      };
    }

    case ROUTER_LOCATION_CHANGE: {
      const pathname = get(action.payload, 'location.pathname');
      const searchParams = new URLSearchParams(get(action.payload, 'location.search'));
      const query = Object.fromEntries(searchParams);

      if (query.processing === '') {
        return {
          ...state,
          show3dSecureFrame: false,
        };
      }

      // if we are not on the payment page/s...
      if (!pathname.includes(routeConstants.PAYMENT)) {
        return {
          ...state,
          submitOrderError: undefined,
          cancelPaypalRedirect: state.paymentProcessing,
          paymentNotSelectedError: undefined,
          paymentNotSelectedErrorMinor: undefined,
          pactPaymentModalOpen: false,
          paymentCardDeleteApiCallFailed: false,
          payPalServiceUnavailable: undefined,
          invalidPayPalBillingAddress: undefined,
          paypalNonce: undefined,
          checkPOSCreditEligibilityProcessing: false,
          showPOSGetReadyModal: false,
        };
      }

      switch (pathname) {
        case routeConstants.PAYMENT_CARD_DELETE: {
          return {
            ...state,
            shouldShowPaymentCardDeleteModal: true,
          };
        }
        case routeConstants.PAYMENT_ADDRESS_BOOK: {
          return {
            ...state,
            showAddressBookModal: true,
            pactPaymentModalOpen: false,
          };
        }
        case routeConstants.PAYMENT_ADDRESS_BOOK_NEW:
          return {
            ...state,
            showAddressBookModal: true,
            pactPaymentModalOpen: false,
          };
        case routeConstants.PAYMENT_ADDRESS_BOOK_EDIT:
          return {
            ...state,
            showAddressBookModal: true,
            pactPaymentStatus: '',
          };
        case routeConstants.PAYMENT_GIFT_OPTIONS:
          return {
            ...state,
            giftOptionsModalOpen: true,
            giftOptionsModalContentId: '',
            pactPaymentModalOpen: false,
          };
        case routeConstants.PAYMENT_PACT:
          return {
            ...state,
            pactPaymentModalOpen: true,
            pactPaymentStatus: '',
          };
        default:
          return {
            ...state,
            showAddressBookModal: false,
            shouldShowPaymentCardDeleteModal: false,
            giftOptionsModalOpen: false,
            pactPaymentStatus: '',
            pactPaymentModalOpen: false,
            paymentProcessing: state.selectedPaymentType === paymentTypeConstants.PAYPAL
              ? false : state.paymentProcessing,
          };
      }
    }

    case REDUX_FORM_SET_SUBMIT_FAILED:
      return {
        ...state,
        paymentFormsValidationError: undefined,
        savedPaymentFormsValidationError: undefined,
      };

    case SET_PROMO_CODE_ERROR: {
      if (action.body) {
        return {
          ...state,
          promoCodeError: {
            title: action.message,
            body: action.body,
          },
        };
      }
      return {
        ...state,
        promoCodeError: action.message,
      };
    }

    case PAYPAL_PAYMENT_FAILED: {
      return {
        ...state,
        submitOrderError: {
          message: 'Sorry, your PayPal payment was unsuccessful',
          body: 'Please try again or try using another payment method',
        },
        selectedPaymentType: undefined,
      };
    }

    case `${GET_DELIVERY_PAGE}.LOADING`:
    case `${POST_DELIVERY_PAGE}.LOADING`:
      return {
        ...state,
        alreadyLoaded: false,
        cancelPaypalRedirect: true,
        paymentProcessing: false,
      };

    case `${SET_PARTNER_DISCOUNT}.SUCCESS`: {
      const paymentWallet = get(action, 'savedPaymentCards', []);
      const defaultSavedPaymentCard = getDefaultPaymentCardOrFirst(paymentWallet);
      let selectedPaymentType = state.selectedPaymentType;

      if (action.partnerDiscountEnabled && state.selectedPaymentType === paymentTypeConstants.PAYPAL) {
        if (isEmpty(paymentWallet)) {
          selectedPaymentType = paymentTypeConstants.CREDIT_CARD;
        } else if (defaultSavedPaymentCard) {
          selectedPaymentType = `${paymentTypeConstants.SAVED_CREDIT_CARD}:${defaultSavedPaymentCard.id}`;
        } else {
          selectedPaymentType = undefined;
        }
      }

      return {
        ...state,
        selectedPaymentType,
        partnerDiscountEnabled: action.partnerDiscountEnabled,
      };
    }

    case SET_CAN_MAKE_APPLE_PAY_PAYMENTS:
      return {
        ...state,
        applePayCompatibilityChecksComplete: true,
      };

    case SET_APPLE_PAY_PAYMENT_REQUEST:
      return {
        ...state,
        applePayPaymentRequest: action.paymentRequest,
      };

    case `${APPLE_PAY_PAYMENT}.SUCCESS`:
      return {
        ...state,
        ocpPaymentId: get(action.result, 'id'),
        applePayPaymentRequest: get(action.result, 'applePayPaymentRequest'),
      };

    case APPLE_PAY_PAYMENT_CANCEL:
      return {
        ...state,
        paymentProcessing: false,
      };

    case APPLE_PAY_PAYMENT_FAILURE:
      return {
        ...state,
        paymentProcessing: false,
        submitOrderError: {
          message: 'Sorry, we are unable to process this payment',
          body: 'Please try again or use another payment method',
        },
      };

    case `${CREATE_GOOGLE_PAY_PAYMENT}.FAILED`:
    case `${GOOGLE_PAY_SUBMIT_PAYMENT_DATA}.FAILED`:
    case GOOGLE_PAY_PAYMENT_DATA_ERROR:
      return {
        ...state,
        paymentProcessing: false,
        submitOrderError: {
          message: 'Sorry, we are unable to process this payment',
          body: 'Please try again or use another payment method',
        },
      };

    case `${APPLE_PAY_REQUEST_PAYMENT_SESSION}.LOADING`:
      return {
        ...state,
        applePayPaymentSessionApiCallActive: true,
      };

    case `${APPLE_PAY_REQUEST_PAYMENT_SESSION}.FAILED`:
    case `${APPLE_PAY_REQUEST_PAYMENT_SESSION}.SUCCESS`:
      return {
        ...state,
        applePayPaymentSessionApiCallActive: false,
      };

    case SET_CAN_MAKE_GOOGLE_PAY_PAYMENTS:
      return {
        ...state,
        canMakeGooglePayPayments: true,
        googlePayBaseCardPaymentMethod: action.baseCardPaymentMethod,
        googlePayCardPaymentMethod: action.cardPaymentMethod,
        googlePayPaymentsClientHasBeenCreated: true,
      };

    case GOOGLE_PAY_PAYMENT_DATA_RECEIVED:
      return {
        ...state,
        googlePayPaymentData: action.paymentData,
        googlePayStubsModalOpen: false,
      };

    case GOOGLE_PAY_CANCEL:
      return {
        ...state,
        googlePayPaymentData: undefined,
        submitOrderError: {
          message: 'Your Google Pay payment was cancelled',
          body: 'Please try again or try using another payment method',
        },
        invalidGooglePayBillingAddress: undefined,
        googlePayStubsModalOpen: false,
        paymentProcessing: false,
      };

    case `${GET_PAYPAL_CLIENT_TOKEN}.LOADING`:
      return {
        ...state,
        getPayPalClientTokenCallActive: true,
        getPayPalClientTokenFailed: false,
      };

    case `${GET_PAYPAL_CLIENT_TOKEN}.SUCCESS`:
      return {
        ...state,
        payPalClientToken: get(action.result, 'token'),
        getPayPalClientTokenCallActive: false,
        getPayPalClientTokenFailed: false,
      };

    case `${GET_PAYPAL_CLIENT_TOKEN}.FAILED`:
      return {
        ...state,
        getPayPalClientTokenCallActive: false,
        getPayPalClientTokenFailed: true,
        showPayPalServiceUnavailableError: get(action, 'showError'),
        payPalServiceUnavailable: true,
        selectedPaymentType: null,
      };

    case `${SET_PAYPAL_SERVICE_UNAVAILABLE}`:
      return {
        ...state,
        payPalServiceUnavailable: true,
        selectedPaymentType: null,
        showPayPalServiceUnavailableError: true,
      };

    case PAYPAL_PAYMENT_CANCEL:
      return {
        ...state,
        selectedPaymentType: undefined,
        submitOrderError: {
          message: 'Your PayPal payment was cancelled',
          body: 'Please try again or try using another payment method',
        },
        invalidPayPalBillingAddress: undefined,
        paypalNonce: undefined,
      };

    case PAYPAL_PAYMENT_STARTED:
      return {
        ...state,
        payPalPaymentProcessing: true,
      };

    case PAYPAL_PAYMENT_ENDED:
      return {
        ...state,
        payPalPaymentProcessing: false,
      };

    case PAYPAL_STUBS_MODAL_OPEN:
      return {
        ...state,
        payPalStubsModalOpen: true,
      };

    case PAYPAL_STUBS_MODAL_CLOSE:
      return {
        ...state,
        payPalStubsModalOpen: false,
      };

    case GOOGLE_PAY_STUBS_MODAL_OPEN:
      return {
        ...state,
        googlePayStubsModalOpen: true,
      };

    case GOOGLE_PAY_WALLET_ELIGIBLE:
      return {
        ...state,
        googlePayWalletEligible: true,
      };

    case SHOW_POS_CREDIT_GET_READY_MODAL:
      return {
        ...state,
        showPOSGetReadyModal: true,
      };

    case SHOW_POS_CREDIT_PROVIDER_UNAVAILABLE_ERROR: {
      const error = getNonGlobalError({
        error: { code: errorCodeConstants.CLIENT_POS_CREDIT_PROVIDER_UNAVAILABLE },
      }, state.submitOrderError);
      return {
        ...state,
        submitOrderError: error,
        selectedPaymentType: null,
      };
    }

    case CLOSE_POS_CREDIT_GET_READY_MODAL:
      return {
        ...state,
        paymentProcessing: false,
        showPOSGetReadyModal: false,
      };

    case `${POS_CREDIT_PAYMENT}.FAILED`: {
      const creditOptionsChanged = includes([
        errorCodeConstants.PAYMENT_POS_CREDIT_OPTIONS_CHANGED,
        errorCodeConstants.PAYMENT_POS_CREDIT_OPTIONS_CHANGED_WITH_ACTION,
        errorCodeConstants.PAYMENT_POS_CREDIT_OPTIONS_CHANGED_BASKET_INVALID,
      ], get(action, 'error.code', ''));

      const submitOrderError = creditOptionsChanged ?
        getNonGlobalError({ error: { code: errorCodeConstants.PAYMENT_POS_CREDIT_OPTIONS_CHANGED } }) :
        getNonGlobalError({ error: { code: errorCodeConstants.CLIENT_POS_CREDIT_PAYMENT_FAILED } });

      return {
        ...state,
        paymentProcessing: false,
        partnerDiscountInvalid: get(action.error, 'code') === errorCodeConstants.PARTNER_CARD_NUMBER_INVALID,
        showPOSGetReadyModal: false,
        submitOrderError: {
          ...submitOrderError,
          isPosCreditError: true,
        },
      };
    }

    case APPLE_PAY_SET_PAYMENT_REQUEST:
      return {
        ...state,
        applePayResponse: action.applePayResponse,
      };

    case APPLE_PAY_SUBMIT_PAYMENT_DATA:
      return {
        ...state,
        applePayResponse: undefined,
      };

    case SET_SELECTED_PAYMENT_CATEGORY: {
      const selectedPaymentType = determineSelectedPaymentType({
        action,
        state,
        selectNewPaymentType: true,
      });

      return {
        ...state,
        paymentCardDeleted: undefined,
        paymentCardDeleteApiCallFailed: undefined,
        selectedPaymentCategory: action.category,
        selectedPaymentType,
        submitOrderError: undefined,
      };
    }

    case SET_SELECTED_SAVED_CARD:
      return {
        ...state,
        selectedSavedCard: action.id,
      };

    case CREATE_PAYPAL_PAYMENT:
      return {
        ...state,
        ...state.streamlinedPaymentsFeatureEnabled && { selectedPaymentType: paymentTypeConstants.PAYPAL },
      };

    case CARD_TYPE_MISSING:
      return {
        ...state,
        submitOrderError: getNonGlobalError(
          { error: { code: errorCodeConstants.CLIENT_CARD_TYPE_MISSING } },
          state.submitOrderError,
        ),
      };

    case GOOGLE_SCRIPT_LOADED:
      return {
        ...state,
        googlePayScriptLoaded: true,
        googlePayScriptLoading: false,
      };

    case GOOGLE_SCRIPT_LOADING:
      return {
        ...state,
        googlePayScriptLoading: true,
      };

    case GOOGLE_SCRIPT_FAILED:
      return {
        ...state,
        googlePayScriptLoading: false,
      };

    case IOVATION_SCRIPTS_LOADED:
      return {
        ...state,
        iovationScriptsLoaded: true,
        iovationScriptsLoading: false,
      };

    case IOVATION_SCRIPTS_LOADING:
      return {
        ...state,
        iovationScriptsLoading: true,
      };

    case IOVATION_SCRIPTS_FAILED:
      return {
        ...state,
        iovationScriptsLoading: false,
      };

    default:
      return state;
  }
}
